import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AuthService } from '../../services/auth.service';
import { AuthState, LoginRequest, RegisterRequest } from '../../types/auth.types';
import { RootState } from '../index';

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await AuthService.login(credentials);
      
      if (response.requiresTwoFactor) {
        return { requiresTwoFactor: true };
      }
      
      // Сохраняем токен и данные пользователя в localStorage
      localStorage.setItem('token', response.access_token);
      localStorage.setItem('user', JSON.stringify(response.user));
      
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка входа');
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData: RegisterRequest, { rejectWithValue }) => {
    try {
      const response = await AuthService.register(userData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка регистрации');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await AuthService.logout();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return null;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка выхода');
    }
  }
);

export const getProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.getProfile();
      return response;
    } catch (error: any) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return rejectWithValue(error.response?.data?.message || 'Ошибка получения профиля');
    }
  }
);

export const generateTwoFactorSecret = createAsyncThunk(
  'auth/generateTwoFactorSecret',
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.generateTwoFactorSecret();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка генерации 2FA');
    }
  }
);

export const enableTwoFactor = createAsyncThunk(
  'auth/enableTwoFactor',
  async (twoFactorCode: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await AuthService.enableTwoFactor(twoFactorCode);
      // Обновляем информацию о пользователе после включения 2FA
      dispatch(getProfile());
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка включения 2FA');
    }
  }
);

export const disableTwoFactor = createAsyncThunk(
  'auth/disableTwoFactor',
  async (twoFactorCode: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await AuthService.disableTwoFactor(twoFactorCode);
      // Обновляем информацию о пользователе после отключения 2FA
      dispatch(getProfile());
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка отключения 2FA');
    }
  }
);

// Начальное состояние
const initialState: AuthState = {
  user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!) : null,
  token: localStorage.getItem('token') || null,
  isAuthenticated: Boolean(localStorage.getItem('token')),
  isLoading: false,
  error: null,
  requiresTwoFactor: false,
};

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthError: (state) => {
      state.error = null;
    },
    resetTwoFactorState: (state) => {
      state.requiresTwoFactor = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        
        if (action.payload.requiresTwoFactor) {
          state.requiresTwoFactor = true;
        } else {
          // Проверяем, что payload имеет свойство 'user'
          if ('user' in action.payload) {
            state.isAuthenticated = true;
            state.user = action.payload.user;
            state.token = action.payload.access_token;
          }
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      .addCase(logout.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      
      // Get Profile
      .addCase(getProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
      });
  },
});

export const { clearAuthError, resetTwoFactorState } = authSlice.actions;

export const selectAuth = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectRequiresTwoFactor = (state: RootState) => state.auth.requiresTwoFactor;

export default authSlice.reducer;